import { useEffect, useMemo, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Paper, Typography, Box } from '@mui/material';
import { useTheme, alpha, type Theme } from '@mui/material/styles';
import type { PSDGeneral, PSDSieveDetail, PSDHydroDetail } from '../../../../types/psd';
import { listSieveNests } from '../../../../services/sieveNestService';
import type { SieveNest } from '../../../../types/sieveNest';
import { buildCurveLive } from '../computations';

interface Props {
  general: PSDGeneral;
  sieveDetails: PSDSieveDetail[];
  hydroDetails: PSDHydroDetail[];
}

// USCS / ASTM soil-classification breakpoints (mm). Used to render the soil
// fraction banner above the gradation curve (BUG-2026-0045).
const MAJOR_FRACTIONS = [
  { from: 300,   to: 1000,  label: 'BOULDER', spanRow: true },
  { from: 75,    to: 300,   label: 'COBBLE',  spanRow: true },
  { from: 4.75,  to: 75,    label: 'GRAVEL',  spanRow: false },
  { from: 0.075, to: 4.75,  label: 'SAND',    spanRow: false },
  { from: 0.001, to: 0.075, label: 'FINES',   spanRow: false },
] as const;

const SUB_FRACTIONS = [
  { from: 19,    to: 75,    label: 'Coarse' }, // gravel coarse
  { from: 4.75,  to: 19,    label: 'Fine'   }, // gravel fine
  { from: 2,     to: 4.75,  label: 'Coarse' }, // sand coarse
  { from: 0.425, to: 2,     label: 'Medium' }, // sand medium
  { from: 0.075, to: 0.425, label: 'Fine'   }, // sand fine
  { from: 0.002, to: 0.075, label: 'SILT'   },
  { from: 0.001, to: 0.002, label: 'CLAY'   },
] as const;

const BANNER_TOP_PAD = 6;
const BANNER_ROW_H = 22;
const BANNER_HEIGHT = BANNER_TOP_PAD + BANNER_ROW_H * 2;
const PLOT_MARGIN_TOP = BANNER_HEIGHT + 28; // banner + room for sieve marker labels

function drawSoilFractionBanner(chart: Highcharts.Chart, theme: Theme) {
  const xAxis = chart.xAxis?.[0];
  if (!xAxis) return;

  // Tear down any previous banner elements (render fires on every redraw).
  const c = chart as Highcharts.Chart & { __soilBanner?: Highcharts.SVGElement[] };
  if (c.__soilBanner) c.__soilBanner.forEach((el) => el.destroy());
  c.__soilBanner = [];

  const borderColor = alpha(theme.palette.text.primary, 0.55);
  const fillColor = alpha(theme.palette.background.paper, 0.4);
  const labelColor = theme.palette.text.secondary;

  const drawCell = (from: number, to: number, y: number, height: number, text: string) => {
    const px1 = xAxis.toPixels(from, false);
    const px2 = xAxis.toPixels(to, false);
    if (!Number.isFinite(px1) || !Number.isFinite(px2)) return;
    const left = Math.min(px1, px2);
    const right = Math.max(px1, px2);
    const plotLeft = chart.plotLeft;
    const plotRight = chart.plotLeft + chart.plotWidth;
    const clippedLeft = Math.max(left, plotLeft);
    const clippedRight = Math.min(right, plotRight);
    const cellW = clippedRight - clippedLeft;
    if (cellW <= 0) return;

    const rect = chart.renderer
      .rect(clippedLeft, y, cellW, height)
      .attr({ stroke: borderColor, 'stroke-width': 1, fill: fillColor })
      .add();
    c.__soilBanner!.push(rect);

    if (text) {
      const label = chart.renderer
        .text(text, clippedLeft + cellW / 2, y + height / 2 + 4)
        .attr({ 'text-anchor': 'middle' })
        .css({ color: labelColor, fontSize: '10px', fontWeight: '600' })
        .add();
      c.__soilBanner!.push(label);
    }
  };

  for (const m of MAJOR_FRACTIONS) {
    const height = m.spanRow ? BANNER_ROW_H * 2 : BANNER_ROW_H;
    drawCell(m.from, m.to, BANNER_TOP_PAD, height, m.label);
  }
  for (const s of SUB_FRACTIONS) {
    drawCell(s.from, s.to, BANNER_TOP_PAD + BANNER_ROW_H, BANNER_ROW_H, s.label);
  }
}

export const GradationCurveChart = ({ general, sieveDetails, hydroDetails }: Props) => {
  const theme = useTheme();
  const [nests, setNests] = useState<SieveNest[]>([]);

  useEffect(() => {
    listSieveNests().then(setNests).catch(() => { /* fail quiet */ });
  }, []);

  const chosenNest = useMemo(
    () => nests.find((n) => n.SieveNestKey === general.sieveNestKey),
    [nests, general.sieveNestKey],
  );

  const options = useMemo((): Highcharts.Options => {
    const curve = buildCurveLive(sieveDetails, hydroDetails, general);
    // Drop any non-positive sizes for log axis
    const data = curve
      .filter((p) => p.sizeMm > 0 && p.passingPct !== null)
      .map((p) => [p.sizeMm, p.passingPct] as [number, number])
      // Highcharts spline wants ascending X
      .sort((a, b) => a[0] - b[0]);

    // Sieve markers — vertical dashed lines at every nest size, with size labels at top
    const sieveSizes = chosenNest?.sizes
      ?.slice()
      .sort((a, b) => b.SieveSizeMm - a.SieveSizeMm)
      ?? [];

    return {
      chart: {
        height: 540,
        backgroundColor: 'transparent',
        spacing: [12, 16, 12, 12],
        marginTop: PLOT_MARGIN_TOP,
        events: {
          render() {
            drawSoilFractionBanner(this, theme);
          },
        },
      },
      title: { text: undefined },
      credits: { enabled: false },
      legend: { enabled: false },
      xAxis: [
        {
          type: 'logarithmic',
          min: 0.001,
          max: 1000,
          reversed: true,    // Particle size: largest on left, smallest on right
          title: { text: 'Particle Size (mm)', style: { fontSize: '11px' } },
          gridLineWidth: 1,
          labels: { style: { fontSize: '10px' } },
          plotLines: sieveSizes.map((s) => ({
            color: alpha(theme.palette.divider, 0.6),
            dashStyle: 'Dash',
            width: 1,
            value: s.SieveSizeMm,
            label: {
              text: s.SieveDisplayName ?? s.SieveDesignation ?? '',
              align: 'center',
              y: -2,
              style: { fontSize: '9px', color: theme.palette.text.secondary, fontWeight: '500' },
            },
            zIndex: 1,
          })),
        },
      ],
      yAxis: {
        min: 0, max: 100,
        title: { text: 'Percent Passing (%)', style: { fontSize: '11px' } },
        tickInterval: 10,
        gridLineWidth: 1,
        labels: { style: { fontSize: '10px' } },
      },
      tooltip: {
        formatter(this: Highcharts.Point) {
          const x = this.x;
          const y = this.y;
          return `Size: ${typeof x === 'number' ? x.toPrecision(3) : x} mm<br/>Passing: ${typeof y === 'number' ? y.toFixed(1) : y}%`;
        },
      },
      series: [
        {
          type: 'spline',  // smooth curve (Q13)
          name: 'Gradation curve',
          data,
          color: theme.palette.primary.main,
          lineWidth: 2,
          marker: { enabled: true, radius: 3, symbol: 'circle' },
          animation: false,
        },
      ],
      plotOptions: {
        series: { animation: false, states: { inactive: { opacity: 1 } } },
      },
    };
  }, [general, sieveDetails, hydroDetails, chosenNest, theme]);

  return (
    <Paper elevation={0} sx={{
      p: 1.5, borderRadius: 2,
      border: `1px solid ${alpha(theme.border?.color ?? '#ccc', 0.35)}`,
      backgroundColor: alpha(theme.palette.background.paper, 0.8),
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: theme.palette.text.primary }}>
          Gradation Curve
        </Typography>
      </Box>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </Paper>
  );
};
